import Contact from 'blocks/contact/contact';
import Gallery from 'blocks/gallery/gallery';
import Heading from 'blocks/heading/heading';
import Image from 'blocks/image/image';
import Links from 'blocks/links/links';
import Paragraph from 'blocks/paragraph/paragraph';
import Partners from 'blocks/partners/partners';
import PullQuote from 'blocks/pullQuote/pullQuote';
import TeamGrid from 'blocks/teamGrid/teamGrid';
import TeamGridSimple from 'blocks/teamGridSimple/teamGridSimple';
import Topics from 'blocks/topics/topics';
import Values from 'blocks/values/values';
import VideoIframe from 'blocks/videoIframe/videoIframe';
import InfoBox from 'blocks/infoBox/infoBox';
import React, { useEffect, useRef } from 'react';
import PageFooterText from 'blocks/pageFooterText/pageFooterText';
import List from 'blocks/list/list';
import Chart from 'components/charts/chart';
import ReadMore from 'blocks/readMore/readMore';
import AnchorLinks from 'blocks/anchorLinks/anchorLinks';
import ForschungDetail from 'blocks/forschungDetail/forschungDetail';
import HeroSmall from 'blocks/heroSmall/heroSmall';
import PostsWithTag from 'blocks/postsWithTag/postsWithTag';
import Form from 'blocks/form/form';
import Source from 'blocks/source/source';
import AuthorLine from 'blocks/authorLine/authorLine';
import DataWrapper from 'blocks/dataWrapper/dataWrapper';
import SidTeasers from 'blocks/sidTeasers/sidTeasers';
import MultiColumnText from 'blocks/multiColumnText/multiColumnText';
import { ShareButtons } from 'blocks/shareButtons/shareButtons';

interface Props {
  blocks: {
    name: string;
    order: number;
  }[];
  postFeeds?: any[];
  glossary?: any[];
  enumHeadlines?: boolean;
}

const blockKeys: any = {
  'core/paragraph': 'attributes',
  'core/heading': 'attributes',
  'core/image': 'attributes',
  'core/pullquote': 'attributes',
  'core/gallery': 'attributes',
  'core/list': null,
  'acf/partners': 'AcfPartners',
  'acf/teamgrid': 'AcfTeamGrid',
  'acf/teamgridsimple': 'AcfTeamGridSimple',
  'acf/values': 'AcfValues',
  'acf/topics': 'AcfTopics',
  'acf/videoiframe': 'AcfVideoIframe',
  'acf/linksblock': 'AcfLinks',
  'acf/contact': 'AcfContact',
  'acf/infobox': 'AcfInfoBlock',
  'acf/pagefootertext': 'AcfPageFooterTextBlock',
  'acf/graph': 'AcfGraph',
  'acf/readmore': 'AcfBlockReadMore',
  'acf/achorlinks': 'AnchorLinks',
  'acf/forschungdetail': 'ForschungDetail',
  'acf/herosmall': 'AcfHeroSmall',
  'acf/postswithtag': 'AcfPostsByTags',
  'acf/form': 'AcfForm',
  'acf/source': 'AcfBlockSource',
  'acf/gallery': 'AcfGalleryBlock',
  'acf/authorline': 'AcfAuthorLine',
  'acf/datawrapper': 'AcfDatawrapper',
  'acf/sidteasers': 'AcfSidTeasers',
  'acf/multicolumntext': 'AcfMultiColumnText',
  'acf/sharebuttons': 'AcfShareButtons',
};

const Components: any = {
  'core/paragraph': Paragraph,
  'core/heading': Heading,
  'core/image': Image,
  'core/pullquote': PullQuote,
  'core/gallery': Gallery,
  'core/list': List,
  'acf/partners': Partners,
  'acf/teamgrid': TeamGrid,
  'acf/teamgridsimple': TeamGridSimple,
  'acf/values': Values,
  'acf/topics': Topics,
  'acf/videoiframe': VideoIframe,
  'acf/linksblock': Links,
  'acf/contact': Contact,
  'acf/infobox': InfoBox,
  'acf/pagefootertext': PageFooterText,
  'acf/graph': Chart,
  'acf/readmore': ReadMore,
  'acf/achorlinks': AnchorLinks,
  'acf/forschungdetail': ForschungDetail,
  'acf/herosmall': HeroSmall,
  'acf/postswithtag': PostsWithTag,
  'acf/form': Form,
  'acf/source': Source,
  'acf/gallery': Gallery,
  'acf/authorline': AuthorLine,
  'acf/datawrapper': DataWrapper,
  'acf/sidteasers': SidTeasers,
  'acf/multicolumntext': MultiColumnText,
  'acf/sharebuttons': ShareButtons,
};

const Blocks: React.FC<Props> = (props) => {
  const h2EnumRef = useRef<number>(0);
  const h3EnumRef = useRef<number>(0);
  const h4EnumRef = useRef<number>(0);

  useEffect(() => {
    h2EnumRef.current = 0;
    h3EnumRef.current = 0;
    h4EnumRef.current = 0;
  }, []);

  return (
    <>
      {props.blocks.map((item, key) => {
        if (key === 0) {
          h2EnumRef.current = 0;
          h3EnumRef.current = 0;
          h4EnumRef.current = 0;
        }

        const compName: string = item.name;
        if (!Components[compName]) {
          return null;
        }
        const component: React.FC<any> = Components[compName];
        // @ts-ignore
        const data: any = !!blockKeys[compName]
          ? // @ts-ignore
            item[blockKeys[compName]]
          : item;
        // add post block
        // console.log(item);
        if (!!props.postFeeds) {
          const postFeed = props.postFeeds.filter((feed) => feed.id === item.order);
          if (!!postFeed.length) {
            data.postFeed = postFeed[0];
          }
        }

        if (compName === 'core/heading' && !data.content.startsWith('<em>') && props.enumHeadlines) {
          let numbering = '';
          if (data.level === 1 || data.level === 2) {
            h2EnumRef.current = h2EnumRef.current + 1;
            h3EnumRef.current = 0;
            h4EnumRef.current = 0;
            numbering = `${h2EnumRef.current}.`;
          } else if (data.level === 3) {
            h4EnumRef.current = 0;
            h3EnumRef.current = h3EnumRef.current + 1;
            numbering = `${h2EnumRef.current}.${h3EnumRef.current}`;
          } else if (data.level === 4) {
            h4EnumRef.current = h4EnumRef.current + 1;
            numbering = `${h2EnumRef.current}.${h3EnumRef.current}.${h4EnumRef.current}`;
          }
          data.numbering = numbering;
        }
        if (compName === 'core/paragraph') {
          data.glossary = props.glossary;
          data.index = key;
        }

        return React.createElement(component, {
          ...data,
          key: key,
        });
      })}
    </>
  );
};

export default Blocks;
